import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUnits } from "@/context/UnitContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, Phone, Mail, AlertCircle, X, StickyNote, Camera, Download, Filter, MessageCircle, Loader2, CheckCircle2, Wallet } from "lucide-react";
import { toast } from "sonner";
import WhatsAppButton from "@/components/WhatsAppButton";
import PageHeader from "@/components/PageHeader";
import ClientNotes from "@/components/ClientNotes";
import ClientAvatar from "@/components/ClientAvatar";
import ClientGalleryDialog from "@/components/ClientGalleryDialog";
import VoiceInputButton from "@/components/VoiceInputButton";

// Helpers para filtros vindos do Dashboard
const parseDate = (s) => (s ? new Date(s) : null);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};
const formatPhone55 = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("55")) return digits;
  return "55" + digits;
};
const FILTER_LABELS = {
  no_haircut: "Sem corte há",
  no_highlights: "Sem luzes há",
  no_service: "Sem o serviço selecionado há",
  absent_week: "Ausentes nos últimos 7 dias",
  absent_month: "Ausentes nos últimos 30 dias",
  birthdays: "Aniversariantes do mês",
  vip: "Clientes VIP",
  no_show: "Já faltaram",
};

// Funil de relacionamento CRM
const FUNNEL_STATUSES = [
  { key: "cinza", label: "Nunca acionado", color: "#6B7280", bg: "#F3F4F6" },
  { key: "amarelo", label: "Campanha educativa", color: "#A16207", bg: "#FEF3C7" },
  { key: "laranja", label: "Convite p/ agenda", color: "#C2410C", bg: "#FFEDD5" },
  { key: "verde", label: "Agendou", color: "#15803D", bg: "#DCFCE7" },
  { key: "azul", label: "Atendimento realizado", color: "#1D4ED8", bg: "#DBEAFE" },
  { key: "vermelho", label: "Sem resposta/perdido", color: "#B91C1C", bg: "#FEE2E2" },
];
const FUNNEL_BY_KEY = Object.fromEntries(FUNNEL_STATUSES.map((f) => [f.key, f]));

export default function Clients() {
  const { apiCall } = useAuth();
  const { units } = useUnits();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [noShows, setNoShows] = useState([]);
  const [loading, setLoading] = useState(true);  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", tags: [], birthday: "" });
  const [notesOpen, setNotesOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState("");
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const [smartLeads, setSmartLeads] = useState([]);
  const [bulkInitialMessage, setBulkInitialMessage] = useState("");

  // Filtros vindos do Dashboard (via URL):
  // ?filter=no_haircut&days=90
  // ?filter=no_service&service=Mecha&days=180
  // ?filter=absent_week | absent_month | birthdays | vip | no_show
  const filterKey = searchParams.get("filter") || "";
  const filterDays = parseInt(searchParams.get("days") || "0", 10);
  const filterServiceQ = (searchParams.get("service") || "").toLowerCase().trim();
  const filterTag = searchParams.get("tag") || "";
  const filterFunnel = searchParams.get("funnel") || "";
  const focusId = searchParams.get("focus") || "";

  // Funnel summary (contagem por status, da unidade ativa)
  const [funnelSummary, setFunnelSummary] = useState(null);
  const loadFunnel = async () => {
    try {
      const r = await apiCall("get", "/funnel/summary");
      setFunnelSummary(r.data);
    } catch { /* ignore */ }
  };


  // Mapeia client_id -> lista de service names já feitos (usado para o ícone fallback)
  const clientServicesMap = useMemo(() => {
    const m = {};
    for (const a of appointments) {
      const cid = a.client_id;
      if (!cid) continue;
      const names = (a.services || []).map((s) => s.service_name || s.name).filter(Boolean);
      if (!m[cid]) m[cid] = [];
      m[cid].push(...names);
    }
    return m;
  }, [appointments]);

  // Para cada cliente: data do último atendimento concluído (geral) + por serviço (substring)
  const clientLastVisit = useMemo(() => {
    const m = {}; // {client_id: {last: Date, lastService: {keyword: Date}}}
    const concluded = (appointments || [])
      .filter((a) => a.status === "concluido" && a.client_id)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    for (const a of concluded) {
      const cid = a.client_id;
      const d = parseDate(a.date);
      if (!m[cid]) m[cid] = { last: d, byService: {} };
      const svcs = (a.services || []).map((s) => (s.service_name || s.name || "").toLowerCase());
      for (const sn of svcs) {
        if (!sn) continue;
        if (!m[cid].byService[sn] || new Date(m[cid].byService[sn]) < d) {
          m[cid].byService[sn] = d;
        }
      }
    }
    return m;
  }, [appointments]);

  // Aplica filtro vindo do Dashboard + busca livre
  const filteredClients = useMemo(() => {
    let list = [...clients];

    // Busca livre
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        [c.name, c.phone, c.email].some((v) => (v || "").toLowerCase().includes(q))
      );
    }

    // Focus único
    if (focusId) {
      list = list.filter((c) => c.client_id === focusId);
      return list;
    }

    const cutoff = filterDays > 0 ? daysAgo(filterDays) : null;
    const now = new Date();

    // Keywords para detectar serviços por categoria (defensivo para nomes reais do salão)
    const KW_CORTE = ["corte"];
    const KW_LUZES = ["luz", "mecha", "highlight", "ombre", "ombré", "balayage", "loira", "platinada", "tonaliza", "sos loira", "morena ilumin"];

    const findLastByKeywords = (lv, kws) => {
      if (!lv) return null;
      let best = null;
      for (const k of Object.keys(lv.byService)) {
        for (const kw of kws) {
          if (k.includes(kw)) {
            const d = lv.byService[k];
            if (!best || d > best) best = d;
            break;
          }
        }
      }
      return best;
    };

    if (filterKey === "no_haircut" && cutoff) {
      // Apenas clientes que JÁ fizeram corte em algum momento E o último é mais antigo que o cutoff.
      // Exclui clientes que nunca fizeram (não são alvo de win-back desse serviço).
      list = list.filter((c) => {
        const lastCut = findLastByKeywords(clientLastVisit[c.client_id], KW_CORTE);
        return lastCut && lastCut < cutoff;
      });
    } else if (filterKey === "no_highlights" && cutoff) {
      list = list.filter((c) => {
        const last = findLastByKeywords(clientLastVisit[c.client_id], KW_LUZES);
        return last && last < cutoff;
      });
    } else if (filterKey === "no_service" && filterServiceQ && cutoff) {
      list = list.filter((c) => {
        const lv = clientLastVisit[c.client_id];
        if (!lv) return false; // nunca fez nada → fora
        let last = null;
        for (const k of Object.keys(lv.byService)) {
          if (k.includes(filterServiceQ)) {
            const d = lv.byService[k];
            if (!last || d > last) last = d;
          }
        }
        return last && last < cutoff;
      });
    } else if (filterKey === "absent_week") {
      // Cliente JÁ teve atendimento mas o último foi há mais de 7 dias
      const c7 = daysAgo(7);
      list = list.filter((c) => {
        const lv = clientLastVisit[c.client_id];
        return lv?.last && lv.last < c7;
      });
    } else if (filterKey === "absent_month") {
      const c30 = daysAgo(30);
      list = list.filter((c) => {
        const lv = clientLastVisit[c.client_id];
        return lv?.last && lv.last < c30;
      });
    } else if (filterKey === "birthdays") {
      const m = now.getMonth();
      list = list.filter((c) => {
        if (!c.birthday) return false;
        const b = new Date(c.birthday);
        return b.getMonth() === m;
      });
    } else if (filterKey === "vip") {
      list = list.filter((c) => (c.tags || []).includes("vip"));
    } else if (filterKey === "no_show") {
      const ids = new Set(noShows.map((n) => n.client_id));
      list = list.filter((c) => ids.has(c.client_id));
    }

    if (filterTag) {
      list = list.filter((c) => (c.tags || []).includes(filterTag));
    }

    if (filterFunnel) {
      list = list.filter((c) => (c.funnel_status || "cinza") === filterFunnel);
    }

    return list;
  }, [clients, search, focusId, filterKey, filterDays, filterServiceQ, filterTag, filterFunnel, clientLastVisit, noShows]);

  const filterDescription = useMemo(() => {
    if (focusId) return "Cliente específico";
    if (filterFunnel) {
      const fs = FUNNEL_BY_KEY[filterFunnel];
      return `Funil: ${fs?.label || filterFunnel}`;
    }
    if (filterKey) {
      const base = FILTER_LABELS[filterKey] || filterKey;
      if (filterKey === "no_haircut" || filterKey === "no_highlights") return `${base} ${filterDays}+ dias`;
      if (filterKey === "no_service") return `${base} "${filterServiceQ}" ${filterDays}+ dias`;
      return base;
    }
    if (search.trim()) return `Busca: "${search.trim()}"`;
    return null;
  }, [filterKey, filterDays, filterServiceQ, focusId, filterFunnel, search]);

  const clearFilter = () => {
    setSearchParams({});
    setSearch("");
  };

  // Mapeia uma "Lista dinâmica / Smart Lead" → URL params do /clientes usando
  // a lógica MAIS AMPLA (mesma de KW_LUZES/KW_CORTE) em vez do regex estrito do
  // endpoint /smart-leads/run que só casa o termo literal. Isso conserta o bug
  // de produção onde "luzes" não retornava clientes porque o serviço estava
  // cadastrado como "Mecha", "Balayage" etc.
  const applySmartLead = (lead) => {
    if (!lead) return;
    const q = (lead.service_query || "").toLowerCase().trim();
    const days = parseInt(lead.days_since_last) || 90;
    const next = new URLSearchParams();
    if (q.includes("corte")) {
      next.set("filter", "no_haircut");
      next.set("days", String(days));
    } else if (
      q.includes("luz") || q.includes("mecha") || q.includes("balayage") ||
      q.includes("highlight") || q.includes("ombre") || q.includes("loira") ||
      q.includes("platinada") || q.includes("tonaliza")
    ) {
      next.set("filter", "no_highlights");
      next.set("days", String(days));
    } else {
      next.set("filter", "no_service");
      next.set("service", q);
      next.set("days", String(days));
    }
    next.set("bulk", "1");
    const msg = lead.message_template || `Oi {primeiro_nome}! Faz um tempinho do seu último ${lead.service_query}. Vamos agendar? 💖`;
    // navigate replace + state pra evitar perda em StrictMode
    navigate(`${location.pathname}?${next.toString()}`, {
      replace: true,
      state: { bulkInitialMessage: msg, from: `smart_lead:${lead.lead_id}` },
    });
  };

  const exportLeadsCSV = () => {
    const rows = filteredClients.map((c) => {
      const lv = clientLastVisit[c.client_id];
      const last = lv?.last ? new Date(lv.last) : null;
      const daysGone = last ? Math.floor((Date.now() - last.getTime()) / 86400000) : "";
      const phone55 = formatPhone55(c.phone);
      const wa = phone55 ? `https://wa.me/${phone55}` : "";
      return {
        nome: c.name,
        telefone: c.phone || "",
        email: c.email || "",
        ultimo_atendimento: last ? last.toISOString().slice(0, 10) : "",
        dias_sem_retorno: daysGone,
        link_whatsapp: wa,
        status_funil: c.funnel_status || "cinza",
        tags: (c.tags || []).join("|"),
      };
    });
    if (rows.length === 0) {
      toast.error("Nenhum cliente para exportar");
      return;
    }
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(";"),
      ...rows.map((r) =>
        headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(";")
      ),
    ].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${(filterKey || "todos")}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} leads exportados`);
  };


  const TAG_OPTIONS = [
    { value: "mecha-teste", label: "Teste de Mechas", color: "#7C3AED", bg: "#EDE9FE" },
    { value: "fechamento-pendente", label: "Fechamento Pendente", color: "#F59E0B", bg: "#FEF3C7" },
    { value: "orcamento", label: "Orçamento", color: "#0EA5E9", bg: "#E0F2FE" },
    { value: "vip", label: "VIP", color: "#CBA871", bg: "#FEF3C7" },
    { value: "fiel", label: "Cliente Fiel", color: "#166534", bg: "#D1FAE5" },
    { value: "novo", label: "Novo Cliente", color: "#3B82F6", bg: "#DBEAFE" },
  ];

  const QUICK_TAGS = ["mecha-teste", "fechamento-pendente", "orcamento"];

  const toggleClientTag = async (client, tag) => {
    const newTags = (client.tags || []).includes(tag)
      ? client.tags.filter((t) => t !== tag)
      : [...(client.tags || []), tag];
    try {
      await apiCall("put", `/clients/${client.client_id}`, {
        name: client.name,
        email: client.email,
        phone: client.phone,
        notes: client.notes,
        tags: newTags,
        birthday: client.birthday,
      });
      toast.success(`Tag ${newTags.includes(tag) ? "adicionada" : "removida"}`);
      load();
    } catch {
      toast.error("Erro ao alterar tag");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, nRes, aRes] = await Promise.all([
        apiCall("get", "/clients"),
        apiCall("get", "/appointments/no-shows"),
        apiCall("get", "/appointments"),
      ]);
      setClients(cRes.data);
      setNoShows(nRes.data);
      setAppointments(aRes.data || []);
    } catch (e) {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadFunnel();
    // Carrega smart leads pra mostrar como atalhos rápidos no filtro
    apiCall("get", "/smart-leads")
      .then((r) => setSmartLeads(r.data || []))
      .catch(() => setSmartLeads([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detecta ?bulk=1 e mensagem pré-carregada (via location.state) → abre o diálogo
  // de disparo automaticamente. Usado quando o usuário vem de "Disparar agora"
  // em Automações ou Leads. Usamos location.state em vez de sessionStorage porque
  // sessionStorage seria consumida 2x em React.StrictMode (dev), perdendo a mensagem.
  useEffect(() => {
    if (searchParams.get("bulk") !== "1") return;
    const initialMsg = location.state?.bulkInitialMessage || "";
    setBulkInitialMessage(initialMsg);
    setBulkSendOpen(true);
    // Limpa o param `bulk` da URL e o state pra evitar reabrir em refresh.
    // navigate(replace) é idempotente sob StrictMode (não consome estado mutável).
    const next = new URLSearchParams(searchParams);
    next.delete("bulk");
    navigate(location.pathname + (next.toString() ? `?${next.toString()}` : ""), {
      replace: true,
      state: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("bulk")]);

  const handleOpenNew = () => {
    setEditing(null);
    setForm({ name: "", email: "", phone: "", notes: "", tags: [], birthday: "" });
    setOpen(true);
  };

  const handleEdit = (client) => {
    setEditing(client);
    setForm({
      name: client.name,
      email: client.email || "",
      phone: client.phone,
      notes: client.notes || "",
      tags: client.tags || [],
      birthday: client.birthday || "",
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, email: form.email || null, birthday: form.birthday || null };
      if (editing) {
        await apiCall("put", `/clients/${editing.client_id}`, payload);
        toast.success("Cliente atualizado");
      } else {
        await apiCall("post", "/clients", payload);
        toast.success("Cliente criado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const toggleTag = (tagValue) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tagValue) ? f.tags.filter((t) => t !== tagValue) : [...f.tags, tagValue],
    }));
  };

  const handleDelete = async (id) => {
    if (!confirm("Deseja excluir este cliente?")) return;
    try {
      await apiCall("delete", `/clients/${id}`);
      toast.success("Cliente excluído");
      load();
    } catch (err) {
      toast.error("Erro ao excluir");
    }
  };

  const updateFunnel = async (clientId, newStatus) => {
    try {
      await apiCall("patch", `/clients/${clientId}/funnel-status`, { status: newStatus });
      setClients((prev) => prev.map((c) => (c.client_id === clientId ? { ...c, funnel_status: newStatus, funnel_updated_at: new Date().toISOString() } : c)));
      loadFunnel();
      toast.success(`Status alterado para ${FUNNEL_BY_KEY[newStatus]?.label || newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar status");
    }
  };

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="clients-page">
      <PageHeader overline="Cadastros" title="Clientes" subtitle="Gerencie sua base de clientes">
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={exportLeadsCSV}
            data-testid="export-leads-button"
            title="Exportar a lista atual como CSV (com link WhatsApp)"
          >
            <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Exportar Leads
          </Button>
          <Button onClick={handleOpenNew} data-testid="add-client-button">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Novo Cliente
          </Button>
        </div>
      </PageHeader>

      {/* Filter banner — quando vindo do Dashboard */}
      {filterDescription && (
        <Card className="border-l-4 border-l-foreground/40 border border-border bg-card p-3 shadow-soft rounded-md flex items-center justify-between gap-3" data-testid="filter-banner">
          <div className="flex items-center gap-2 text-sm">
            <Filter className="h-4 w-4 text-foreground/70" strokeWidth={1.5} />
            <span className="text-muted-foreground uppercase tracking-widest text-xs">Filtro ativo:</span>
            <span className="font-medium">{filterDescription}</span>
            <span className="text-xs text-muted-foreground">({filteredClients.length} cliente{filteredClients.length !== 1 ? "s" : ""})</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setBulkSendOpen(true)}
              disabled={filteredClients.length === 0}
              style={{ background: "#16a34a" }}
              className="text-white hover:opacity-90"
              data-testid="filter-bulk-send"
            >
              <MessageCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
              Disparar WhatsApp ({filteredClients.length})
            </Button>
            <Button variant="ghost" size="sm" onClick={clearFilter} data-testid="filter-clear">
              <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
              Limpar
            </Button>
          </div>
        </Card>
      )}

      {/* Search bar */}
      <div className="flex items-center gap-2 max-w-md">
        <Input
          placeholder="Buscar por nome, telefone ou email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="client-search"
          className="text-sm"
        />
        {search && (
          <Button variant="ghost" size="sm" onClick={() => setSearch("")} title="Limpar busca">
            <X className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        )}
      </div>

      {/* Listas dinâmicas removidas: tudo unificado em /automacoes (Filtros e Leads) */}

      {/* Funil CRM — clicável p/ filtrar */}
      <Card className="border border-border bg-card p-3 shadow-soft rounded-md" data-testid="funnel-bar">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">📊 Funil de Relacionamento</div>
          {filterFunnel && (
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("funnel");
                setSearchParams(next);
              }}
              className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
              data-testid="funnel-clear"
            >
              limpar filtro funil
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {FUNNEL_STATUSES.map((s) => {
            const count = funnelSummary?.[s.key] ?? 0;
            const active = filterFunnel === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  if (active) next.delete("funnel");
                  else next.set("funnel", s.key);
                  setSearchParams(next);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-all ${active ? "ring-2 ring-foreground/40 shadow-sm" : "hover:opacity-80"}`}
                style={{ background: s.bg, color: s.color }}
                data-testid={`funnel-chip-${s.key}`}
                title={`${s.label} (${count})`}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                <span className="font-medium">{s.label}</span>
                <span className="text-[10px] opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* No-shows alert block */}
      {noShows.length > 0 && (
        <Card
          className="border-l-4 border-l-destructive border border-border bg-card p-5 shadow-soft rounded-md"
          data-testid="no-shows-block"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "#FEE2E2" }}>
              <AlertCircle className="h-5 w-5" strokeWidth={1.5} style={{ color: "#991B1B" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest font-medium mb-1" style={{ color: "#991B1B" }}>
                Atenção · Clientes que faltaram nos últimos 90 dias
              </div>
              <div className="text-sm text-muted-foreground mb-3">
                Considere entrar em contato para reagendar ou avaliar política de no-show
              </div>
              <div className="flex flex-wrap gap-2">
                {noShows.slice(0, 10).map((ns) => (
                  <div
                    key={ns.client_id}
                    className="bg-secondary/60 border border-border rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                    data-testid={`no-show-${ns.client_id}`}
                  >
                    <span className="font-medium">{ns.client_name}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: "#FEE2E2", color: "#991B1B" }}>
                      {ns.count}× faltou
                    </span>
                  </div>
                ))}
                {noShows.length > 10 && (
                  <span className="text-xs text-muted-foreground self-center">
                    +{noShows.length - 10} mais
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Contato</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Tags</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Observações</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                  {filterDescription ? "Nenhum cliente bate com o filtro." : "Nenhum cliente cadastrado ainda."}
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((c) => (
                <TableRow key={c.client_id} data-testid={`client-row-${c.client_id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <ClientAvatar
                        client={c}
                        services={clientServicesMap[c.client_id] || []}
                        size={42}
                        onClick={() => { setSelectedClient(c); setGalleryOpen(true); }}
                      />
                      <div className="flex flex-col gap-1 min-w-0">
                        <span className="truncate">{c.name}</span>
                        {(() => {
                          const fs = FUNNEL_BY_KEY[c.funnel_status || "cinza"] || FUNNEL_BY_KEY.cinza;
                          return (
                            <select
                              value={c.funnel_status || "cinza"}
                              onChange={(e) => updateFunnel(c.client_id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-5 text-[10px] uppercase tracking-wider rounded px-1.5 border-0 cursor-pointer font-medium"
                              style={{ background: fs.bg, color: fs.color }}
                              title="Status no funil de relacionamento"
                              data-testid={`funnel-select-${c.client_id}`}
                            >
                              {FUNNEL_STATUSES.map((s) => (
                                <option key={s.key} value={s.key}>● {s.label}</option>
                              ))}
                            </select>
                          );
                        })()}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                        {c.phone}
                      </div>
                      {c.email && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" strokeWidth={1.5} />
                          {c.email}
                        </div>
                      )}
                      {(() => {
                        const lv = clientLastVisit[c.client_id];
                        if (!lv?.last) return (
                          <div className="text-[10px] uppercase tracking-widest text-orange-700">
                            Nunca atendida
                          </div>
                        );
                        const days = Math.floor((Date.now() - new Date(lv.last).getTime()) / 86400000);
                        const cls = days > 180 ? "text-red-700" : days > 90 ? "text-orange-700" : "text-emerald-700";
                        return (
                          <div className={`text-[10px] uppercase tracking-widest ${cls}`} data-testid={`client-last-visit-${c.client_id}`}>
                            {days} dia{days !== 1 ? "s" : ""} s/ retorno
                          </div>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(c.tags || []).map((tag) => {
                        const t = TAG_OPTIONS.find((o) => o.value === tag);
                        return (
                          <span
                            key={tag}
                            className="text-xs uppercase tracking-widest font-medium px-1.5 py-0.5 rounded"
                            style={{ background: t?.bg || "#F3F4F6", color: t?.color || "#737373" }}
                            data-testid={`client-tag-${tag}`}
                          >
                            {t?.label || tag}
                          </span>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-1" data-testid={`quick-tags-${c.client_id}`}>
                      {QUICK_TAGS.map((qt) => {
                        const t = TAG_OPTIONS.find((o) => o.value === qt);
                        const active = (c.tags || []).includes(qt);
                        return (
                          <button
                            key={qt}
                            type="button"
                            onClick={() => toggleClientTag(c, qt)}
                            className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border transition-colors"
                            style={{
                              background: active ? t.bg : "transparent",
                              color: active ? t.color : "#9CA3AF",
                              borderColor: active ? t.color : "#E5E7EB",
                            }}
                            title={active ? `Remover ${t.label}` : `Marcar ${t.label}`}
                            data-testid={`quick-tag-${qt}-${c.client_id}`}
                          >
                            {active ? "✓" : "+"} {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                    {c.notes || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <WhatsAppButton
                      toPhone={c.phone}
                      message={`Oi ${c.name}! 💖`}
                      testId={`whatsapp-client-${c.client_id}`}
                    />
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedClient(c); setGalleryOpen(true); }} title="Foto e anexos" data-testid={`gallery-client-${c.client_id}`}>
                      <Camera className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedClient(c); setNotesOpen(true); }} title="Notas e Lembretes" data-testid={`notes-client-${c.client_id}`}>
                      <StickyNote className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setSelectedClient(c); setCreditOpen(true); }}
                      title={`Crédito · R$ ${Number(c.credit_balance || 0).toFixed(2)}`}
                      data-testid={`credit-client-${c.client_id}`}
                      className="relative"
                    >
                      <Wallet
                        className={`h-4 w-4 ${Number(c.credit_balance || 0) > 0 ? "text-emerald-600" : ""}`}
                        strokeWidth={1.5}
                      />
                      {Number(c.credit_balance || 0) > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500" />
                      )}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(c)} data-testid={`edit-client-${c.client_id}`}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(c.client_id)} data-testid={`delete-client-${c.client_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              {editing ? "Editar Cliente" : "Novo Cliente"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="client-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+5511999999999" required data-testid="client-phone-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email (opcional)</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Aniversário (opcional)</Label>
              <Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="client-birthday-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest mb-2 block">Tags / Categorias</Label>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => toggleTag(t.value)}
                    className="text-xs uppercase tracking-widest px-2 py-1 rounded font-medium transition-all border"
                    style={{
                      background: form.tags.includes(t.value) ? t.bg : "transparent",
                      color: form.tags.includes(t.value) ? t.color : "#737373",
                      borderColor: form.tags.includes(t.value) ? t.color : "#E5E5E5",
                    }}
                    data-testid={`client-tag-toggle-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                Observações
                <VoiceInputButton
                  size="sm"
                  testId="client-notes-voice"
                  onTranscript={(text) => setForm((f) => ({ ...f, notes: text }))}
                />
              </Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes-input" />
            </div>
            {editing && (
              <ClientUnitReassignBlock
                client={editing}
                units={units}
                onReassigned={() => { setOpen(false); load(); }}
              />
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="client-save-button">
                {editing ? "Atualizar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              Anotações & Lembretes
              <div className="text-sm text-muted-foreground font-normal mt-1">
                {selectedClient?.name}
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="mt-2">
              <ClientNotes clientId={selectedClient.client_id} clientName={selectedClient.name} embedded={true} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ClientGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        client={selectedClient}
        services={selectedClient ? (clientServicesMap[selectedClient.client_id] || []) : []}
        onUpdated={load}
      />

      <ClientCreditDialog
        open={creditOpen}
        onClose={() => setCreditOpen(false)}
        client={selectedClient}
        onUpdated={load}
      />

      <BulkSendFromClientsDialog
        open={bulkSendOpen}
        onClose={() => { setBulkSendOpen(false); setBulkInitialMessage(""); }}
        clients={filteredClients}
        filterDescription={filterDescription}
        initialMessage={bulkInitialMessage}
      />
    </div>
  );
}

/* ============ Bulk WhatsApp Send: dispara para uma lista de clientes (vinda de filtro) ============ */

const FUNNEL_LABELS_BULK = {
  amarelo: "🟡 Educativa",
  laranja: "🟠 Convite agenda",
  verde: "🟢 Agendou",
  azul: "🔵 Atendida",
  vermelho: "🔴 Sem resposta",
};

function BulkSendFromClientsDialog({ open, onClose, clients, filterDescription, initialMessage }) {
  const { apiCall } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loadingTpls, setLoadingTpls] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [adhocText, setAdhocText] = useState("");
  const [funnelOnSend, setFunnelOnSend] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [waConnected, setWaConnected] = useState(null);
  const [attachment, setAttachment] = useState(null); // {data_url, kind, file_name, mime, size}
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [dedupeHours, setDedupeHours] = useState(24);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setTemplateId("");
      setAdhocText("");
      setFunnelOnSend("");
      setAttachment(null);
      setDedupeHours(24);
      return;
    }
    // Pre-preenche a mensagem se foi passada (ex: vindo de "Disparar agora" de Lista Dinâmica)
    if (initialMessage) setAdhocText(initialMessage);
    setLoadingTpls(true);
    Promise.all([
      apiCall("get", "/campaign-templates").catch(() => ({ data: [] })),
      apiCall("get", "/whatsapp/status").catch(() => ({ data: { connected: false } })),
    ])
      .then(([tplsR, statusR]) => {
        setTemplates(tplsR.data || []);
        setWaConnected(!!statusR.data?.connected);
      })
      .finally(() => setLoadingTpls(false));
  }, [open, apiCall, initialMessage]);

  const handleAttach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Arquivo grande demais (máximo 15MB)");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setUploadingAtt(true);
    try {
      const backendURL = process.env.REACT_APP_BACKEND_URL;
      const tokenHeaders = {};
      const unitId = localStorage.getItem("selected_unit_id");
      if (unitId && unitId !== "all") tokenHeaders["X-Unit-Id"] = unitId;
      const res = await fetch(`${backendURL}/api/whatsapp/upload-media`, {
        method: "POST",
        credentials: "include",
        headers: tokenHeaders,
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setAttachment(data);
      toast.success(`Anexo carregado: ${data.file_name}`);
    } catch (err) {
      toast.error(err.message || "Erro ao subir anexo");
    } finally {
      setUploadingAtt(false);
      e.target.value = "";
    }
  };

  const eligibleClients = useMemo(
    () => (clients || []).filter((c) => (c.phone || "").trim()),
    [clients]
  );
  const skippedNoPhone = (clients || []).length - eligibleClients.length;

  const selectedTpl = templates.find((t) => t.template_id === templateId);
  const previewText = useMemo(() => {
    const baseText = selectedTpl?.text ?? adhocText ?? "";
    const sample = eligibleClients[0];
    const nome = sample?.name || "Cliente";
    return baseText
      .replaceAll("{nome}", nome)
      .replaceAll("{{nome}}", nome)
      .replaceAll("{primeiro_nome}", nome.split(" ")[0] || "")
      .replaceAll("{{primeiro_nome}}", nome.split(" ")[0] || "");
  }, [selectedTpl, adhocText, eligibleClients]);

  const submit = async () => {
    if (eligibleClients.length === 0) {
      toast.error("Nenhum cliente com telefone na lista");
      return;
    }
    if (!templateId && !adhocText.trim()) {
      toast.error("Escolha um template ou escreva uma mensagem");
      return;
    }
    setSending(true);
    try {
      const isImage = attachment && attachment.kind === "image";
      const isDoc = attachment && attachment.kind === "document";
      const r = await apiCall("post", "/whatsapp/campaigns/bulk-send", {
        template_id: templateId || null,
        message: templateId ? null : adhocText.trim(),
        client_ids: eligibleClients.map((c) => c.client_id),
        funnel_status_on_send: funnelOnSend || null,
        image_base64: isImage ? attachment.data_url : null,
        document_url: isDoc ? attachment.data_url : null,
        document_file_name: isDoc ? attachment.file_name : null,
        dedupe_window_hours: dedupeHours,
      });
      setResult(r.data);
      toast.success(`${r.data.queued} mensagens na fila`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao disparar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="clients-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
            Disparar WhatsApp em massa
          </DialogTitle>
          <DialogDescription className="text-xs">
            Envia mensagens em fila com delay anti-ban (8-30s). Variáveis {"{nome}"} e {"{primeiro_nome}"} são substituídas automaticamente.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 text-sm">
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
                <span className="font-medium text-emerald-900">Campanha disparada!</span>
              </div>
              <p className="text-xs text-emerald-900">
                <b>{result.queued}</b> mensagens na fila · estimativa <b>~{result.estimated_minutes} min</b>
                {" "}com delay anti-ban (8–30s entre cada envio).
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">Job ID: <code>{result.job_id}</code></p>
            </div>
            {(result.skipped || []).length > 0 && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs">
                <div className="font-medium text-amber-900 mb-1">{result.skipped.length} pulado(s):</div>
                {result.skipped.slice(0, 8).map((s, i) => (
                  <div key={i} className="text-amber-800">· {s.client_name} — {s.reason}</div>
                ))}
                {result.skipped.length > 8 && (
                  <div className="text-amber-800">… +{result.skipped.length - 8} mais</div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={onClose} data-testid="clients-bulk-close">Fechar</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {waConnected === false && (
              <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
                ⚠️ WhatsApp não está conectado na Z-API. Configure antes de disparar.
              </div>
            )}

            <div className="p-3 rounded-md bg-secondary/50 border border-border space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Público-alvo</div>
              <div className="text-sm">
                <b>{eligibleClients.length}</b> cliente{eligibleClients.length !== 1 ? "s" : ""} elegível{eligibleClients.length !== 1 ? "is" : ""}
                {filterDescription && <span className="text-muted-foreground"> · filtro: {filterDescription}</span>}
              </div>
              {skippedNoPhone > 0 && (
                <div className="text-[11px] text-amber-700">
                  {skippedNoPhone} cliente{skippedNoPhone !== 1 ? "s" : ""} sem telefone (pulado{skippedNoPhone !== 1 ? "s" : ""})
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs">Modelo de mensagem</Label>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={loadingTpls}
                className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
                data-testid="clients-bulk-template"
              >
                <option value="">— Mensagem livre (digite abaixo) —</option>
                {templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.name}{t.category ? ` · ${t.category}` : ""}
                  </option>
                ))}
              </select>
            </div>

            {!templateId && (
              <div>
                <Label className="text-xs">Mensagem livre</Label>
                <textarea
                  rows={4}
                  value={adhocText}
                  onChange={(e) => setAdhocText(e.target.value)}
                  placeholder="Olá {nome}, tudo bem? Aqui é do salão Gregory João..."
                  className="w-full px-2 py-1.5 text-xs border border-input rounded-md bg-background resize-none"
                  data-testid="clients-bulk-adhoc"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Use <code className="bg-secondary px-1 rounded">{"{nome}"}</code> ou <code className="bg-secondary px-1 rounded">{"{primeiro_nome}"}</code> pra personalizar.
                </p>
              </div>
            )}

            {(selectedTpl || adhocText) && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Pré-visualização (1º cliente: {eligibleClients[0]?.name || "—"})
                </Label>
                <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-xs whitespace-pre-wrap min-h-[60px]">
                  {previewText || "—"}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Atualizar funil após envio</Label>
              <select
                value={funnelOnSend}
                onChange={(e) => setFunnelOnSend(e.target.value)}
                className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
                data-testid="clients-bulk-funnel-update"
              >
                <option value="">— Não alterar —</option>
                {Object.entries(FUNNEL_LABELS_BULK).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </div>

            {/* Anexo (imagem ou PDF) */}
            <div>
              <Label className="text-xs">Anexo (opcional, máx 15MB)</Label>
              {attachment ? (
                <div className="flex items-center justify-between gap-2 p-2 bg-secondary/50 rounded border border-border text-xs">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {attachment.kind === "image" ? (
                      <img src={attachment.data_url} alt="preview" className="h-10 w-10 object-cover rounded" />
                    ) : (
                      <span className="text-2xl">📄</span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{attachment.file_name}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {(attachment.size / 1024).toFixed(0)} KB · {attachment.kind}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setAttachment(null)} data-testid="clients-bulk-attach-remove">
                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              ) : (
                <label className="flex items-center gap-2 p-2 bg-background border border-dashed border-border rounded cursor-pointer hover:bg-secondary/40">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleAttach}
                    className="hidden"
                    disabled={uploadingAtt}
                    data-testid="clients-bulk-attach-input"
                  />
                  {uploadingAtt ? (
                    <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /><span className="text-xs">Carregando…</span></>
                  ) : (
                    <span className="text-xs text-muted-foreground">+ Anexar imagem ou PDF</span>
                  )}
                </label>
              )}
            </div>

            {/* Dedupe window */}
            <div>
              <Label className="text-xs">Não enviar para quem já recebeu nas últimas</Label>
              <div className="flex items-center gap-2">
                <select
                  value={dedupeHours}
                  onChange={(e) => setDedupeHours(parseInt(e.target.value))}
                  className="h-8 px-2 text-xs border border-input rounded-md bg-background"
                  data-testid="clients-bulk-dedupe"
                >
                  <option value="0">Desativar (enviar pra todos)</option>
                  <option value="6">6 horas</option>
                  <option value="24">24 horas</option>
                  <option value="72">3 dias</option>
                  <option value="168">7 dias</option>
                  <option value="720">30 dias</option>
                </select>
                <span className="text-[10px] text-muted-foreground">
                  Evita duplicar envios pra mesma pessoa.
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={sending}>Cancelar</Button>
              <Button
                onClick={submit}
                disabled={sending || eligibleClients.length === 0 || (!templateId && !adhocText.trim()) || waConnected === false}
                style={{ background: "#16a34a" }}
                className="text-white hover:opacity-90"
                data-testid="clients-bulk-submit"
              >
                {sending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />Disparando...</>
                ) : (
                  <><MessageCircle className="h-4 w-4 mr-2" strokeWidth={1.5} />Disparar para {eligibleClients.length}</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ============ Client Credit Dialog: saldo, depósito avulso e histórico do ledger ============ */

const CREDIT_TYPE_META = {
  deposit:          { label: "Depósito",          color: "text-emerald-700", bg: "bg-emerald-50", sign: "+" },
  spent:            { label: "Consumo",           color: "text-red-700",     bg: "bg-red-50",     sign: ""  },
  refund:           { label: "Devolução",         color: "text-blue-700",    bg: "bg-blue-50",    sign: "+" },
  deposit_reversal: { label: "Estorno depósito",  color: "text-orange-700",  bg: "bg-orange-50",  sign: ""  },
  opening_balance:  { label: "Saldo inicial",     color: "text-gray-700",    bg: "bg-gray-50",    sign: "+" },
};

const CREDIT_METHOD_LABEL = {
  dinheiro: "Dinheiro", pix: "PIX", credito: "Crédito", debito: "Débito",
  transferencia: "Transferência", credito_cliente: "Crédito do Cliente",
};

function ClientCreditDialog({ open, onClose, client, onUpdated }) {
  const { apiCall } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [depositMode, setDepositMode] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("dinheiro");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!client) return;
    setLoading(true);
    try {
      const [balRes, ledgerRes] = await Promise.all([
        apiCall("get", `/clients/${client.client_id}/credit-balance`),
        apiCall("get", `/clients/${client.client_id}/credit-ledger?limit=100`),
      ]);
      setData({ balance: balRes.data, entries: ledgerRes.data || [] });
    } catch (e) {
      toast.error("Erro ao carregar crédito");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && client) {
      setDepositMode(false);
      setAmount("");
      setMethod("dinheiro");
      setNotes("");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client]);

  const submitDeposit = async () => {
    const amt = parseFloat((amount || "").replace(",", "."));
    if (!(amt > 0)) {
      toast.error("Valor inválido");
      return;
    }
    setBusy(true);
    try {
      await apiCall("post", `/clients/${client.client_id}/credit-deposit`, {
        amount: amt,
        payment_method: method,
        notes: notes || null,
      });
      toast.success(`Depósito de R$ ${amt.toFixed(2)} registrado`);
      setDepositMode(false);
      setAmount("");
      setNotes("");
      await load();
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao depositar");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !client) return null;

  const bal = data?.balance;
  const entries = data?.entries || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-credit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-light tracking-tight text-2xl">
            <Wallet className="h-5 w-5" strokeWidth={1.5} />
            Crédito do cliente
          </DialogTitle>
          <DialogDescription className="text-xs">{client.name}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Saldo atual */}
            <div className="p-4 rounded-lg border bg-gradient-to-br from-emerald-50 to-white">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800">Saldo disponível</div>
              <div className="text-3xl font-light text-emerald-700 mt-1" data-testid="credit-balance-value">
                R$ {Number(bal?.credit_balance || 0).toFixed(2)}
              </div>
              {bal?.totals && (
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Depositado</div>
                    <div className="font-medium text-emerald-700">R$ {Number(bal.totals.deposits || 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Consumido</div>
                    <div className="font-medium text-red-700">R$ {Number(bal.totals.spent || 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Devolvido</div>
                    <div className="font-medium text-blue-700">R$ {Number(bal.totals.refunds || 0).toFixed(2)}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Depósito avulso */}
            {!depositMode ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDepositMode(true)}
                className="w-full"
                data-testid="deposit-toggle-btn"
              >
                <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />
                Depositar crédito avulso
              </Button>
            ) : (
              <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50/40 space-y-2">
                <div className="text-xs font-medium text-emerald-900">Novo depósito de crédito</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Valor (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0,00"
                      data-testid="deposit-amount"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Forma</Label>
                    <select
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="deposit-method"
                    >
                      <option value="dinheiro">Dinheiro</option>
                      <option value="pix">PIX</option>
                      <option value="credito">Crédito</option>
                      <option value="debito">Débito</option>
                      <option value="transferencia">Transferência</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Observação (opcional)</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ex: pré-pago pacote de esmaltação"
                    data-testid="deposit-notes"
                  />
                </div>
                <div className="text-[11px] text-emerald-900/80 leading-relaxed">
                  ℹ️ Este valor <b>NÃO entra como receita</b> — é um passivo (dinheiro adiantado). A receita será
                  reconhecida somente quando o cliente consumir o crédito em atendimentos.
                </div>
                <div className="flex gap-2 justify-end pt-1">
                  <Button size="sm" variant="ghost" onClick={() => setDepositMode(false)} disabled={busy}>
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={submitDeposit}
                    disabled={busy || !amount}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="deposit-confirm-btn"
                  >
                    {busy ? "Salvando…" : "Confirmar depósito"}
                  </Button>
                </div>
              </div>
            )}

            {/* Histórico */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Histórico ({entries.length})
              </div>
              {entries.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-6">Nenhum movimento ainda.</div>
              ) : (
                <div className="space-y-1.5">
                  {entries.map((e) => {
                    const meta = CREDIT_TYPE_META[e.type] || { label: e.type, color: "text-gray-700", bg: "bg-gray-50", sign: "" };
                    const voided = !!e.voided_at;
                    return (
                      <div
                        key={e.ledger_id}
                        className={`p-2 rounded border text-xs ${meta.bg} ${voided ? "opacity-50" : ""}`}
                        data-testid={`ledger-entry-${e.ledger_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${meta.color}`}>{meta.label}</span>
                            {voided && <Badge variant="outline" className="text-[9px]">ESTORNADO</Badge>}
                          </div>
                          <div className={`font-medium ${meta.color}`}>
                            {meta.sign}{Number(e.amount || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-0.5 text-[10px] text-muted-foreground">
                          <span>
                            {new Date(e.created_at).toLocaleString("pt-BR")}
                            {e.payment_method ? ` · ${CREDIT_METHOD_LABEL[e.payment_method] || e.payment_method}` : ""}
                            {e.created_by_name ? ` · ${e.created_by_name}` : ""}
                          </span>
                          <span>saldo após: R$ {Number(e.balance_after || 0).toFixed(2)}</span>
                        </div>
                        {e.notes && (
                          <div className="text-[10px] text-muted-foreground italic mt-0.5">{e.notes}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* ============ Trocar Unidade de um cliente ============ */
function ClientUnitReassignBlock({ client, units, onReassigned }) {
  const { apiCall } = useAuth();
  const currentUnitId = client?.unit_id || "";
  const currentUnitName =
    (units.find((u) => u.unit_id === currentUnitId)?.name) || (currentUnitId ? "(unidade desconhecida)" : "sem unidade");
  const activeUnits = (units || []).filter((u) => u.active !== false);
  const [expanded, setExpanded] = useState(false);
  const [newUnitId, setNewUnitId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!newUnitId) { toast.error("Selecione a nova unidade"); return; }
    if (newUnitId === currentUnitId) { toast.error("Escolha uma unidade diferente da atual"); return; }
    if (!window.confirm(`Mover o cliente "${client.name}" para outra unidade?`)) return;
    setSaving(true);
    try {
      const r = await apiCall("patch", `/clients/${client.client_id}/unit`, {
        new_unit_id: newUnitId,
        reason: reason.trim() || null,
      });
      toast.success(`Movido para ${r.data?.new_unit_name || "nova unidade"} · rollback: ${r.data?.run_id}`);
      onReassigned && onReassigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao trocar unidade");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t pt-3 mt-3" data-testid="client-unit-reassign-block">
      <div className="flex items-center justify-between">
        <div className="text-xs">
          <span className="uppercase tracking-widest text-muted-foreground">Unidade atual:</span>{" "}
          <span className="font-medium" data-testid="client-current-unit">{currentUnitName}</span>
        </div>
        {!expanded && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setExpanded(true)}
            data-testid="open-unit-reassign-btn"
          >
            Trocar unidade
          </Button>
        )}
      </div>
      {expanded && (
        <div className="mt-3 space-y-2 bg-slate-50 border rounded p-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">Nova unidade</Label>
            <select
              value={newUnitId}
              onChange={(e) => setNewUnitId(e.target.value)}
              className="h-9 w-full mt-1 border rounded-md px-2 text-sm bg-background"
              data-testid="unit-reassign-select"
            >
              <option value="">— Selecione —</option>
              {activeUnits.filter((u) => u.unit_id !== currentUnitId).map((u) => (
                <option key={u.unit_id} value={u.unit_id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Motivo (opcional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: cliente passou a ser atendida no Carrão"
              data-testid="unit-reassign-reason"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => { setExpanded(false); setNewUnitId(""); setReason(""); }}>
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={submit}
              disabled={saving || !newUnitId}
              data-testid="unit-reassign-confirm-btn"
            >
              {saving ? "Movendo…" : "Confirmar troca"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
